import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { User } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { NavigationEnd, Router } from '@angular/router';
import { TokenStorageService } from 'viewbid-shared-lib';
import { VbHeaderMenuItem, VbHeaderNavItem, VbHeaderUser } from '@viewbid/ui';
import { menu_data } from '@/shared/data/menu-data';
import { BiddingUtils } from '@/vb-utils/bidding.util';
import {
    selectIsAdmin,
    selectUser,
} from '../../../../../viewbid/state/app-selectors';
import { AppState } from '../../../../../viewbid/state/app-state';
import { selectWatchlistPageState } from 'src/app/viewbid/modules/profiles/state/watchlist-page-state/watchlist-page-state.selectors';
import { WatchlistPageState } from 'src/app/viewbid/modules/profiles/state/watchlist-page-state/watchlist-page-state.reducer';
import { watchlistUserChanged } from 'src/app/viewbid/modules/profiles/state/watchlist-page-state/watchlist-page-state.actions';
import { IMenuItem } from '@/types/menu-d-type';

/**
 * App-shell wrapper around the @viewbid/ui vb-app-header (replaces the legacy
 * vb-header-two): adapts store/auth state to the header's inputs, routes its
 * events, and projects the app's live-search typeahead and self-contained
 * notifications bell into the header's slots.
 */
@Component({
    selector: 'vb-app-header-shell',
    templateUrl: './app-header-shell.component.html',
    styleUrls: ['./app-header-shell.component.scss'],
})
export class AppHeaderShellComponent implements OnInit, OnDestroy {
    /** Primary nav — same source as the legacy header (menu-data). Ids are the
     *  route paths, so navSelect can navigate directly. */
    readonly navItems: VbHeaderNavItem[] = menu_data.map((item: IMenuItem) => ({
        id: String(item.link ?? ''),
        label: String(item.title ?? ''),
    }));

    readonly menuItems: VbHeaderMenuItem[] = [
        { id: '/profiles/my-profile', label: 'My Profile' },
    ];

    user: User | null = null;
    headerUser: VbHeaderUser | null = null;
    activeNavId = '';
    watchCount = 0;
    /** Admins get an "Admin Area" link in the header. The admin app is served
     *  same-origin under /admin/, so a root-relative URL works in every
     *  environment. */
    isAdmin = false;
    readonly adminHref: string = '/admin/';

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly store: Store<AppState>,
        private readonly router: Router,
        private readonly biddingUtils: BiddingUtils,
        private readonly tokenStorageService: TokenStorageService,
    ) {}

    ngOnInit(): void {
        this.store
            .select<User | null>(selectUser)
            .pipe(takeUntil(this.destroy$))
            .subscribe((user: User | null) => {
                this.user = user;
                this.headerUser = user ? this.toHeaderUser(user) : null;
            });

        this.store
            .select(selectIsAdmin)
            .pipe(takeUntil(this.destroy$))
            .subscribe((isAdmin: boolean) => {
                this.isAdmin = isAdmin;
            });

        this.store
            .select(selectWatchlistPageState)
            .pipe(takeUntil(this.destroy$))
            .subscribe((state: WatchlistPageState) => {
                // Keep the previous count while a reload is in flight — the
                // legacy header likewise suppressed the badge mid-load.
                if (!state?.loading) {
                    this.watchCount = state?.watchlist?.totalCount ?? 0;
                }
            });

        this.biddingUtils
            .getCurrentUserId()
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((currentUserId: string) => {
                if (currentUserId) {
                    this.store.dispatch(watchlistUserChanged());
                }
            });

        this.syncFromUrl(this.router.url);
        this.router.events
            .pipe(
                filter(
                    (event: any): event is NavigationEnd =>
                        event instanceof NavigationEnd,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((event: NavigationEnd) => {
                this.syncFromUrl(event.urlAfterRedirects);
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onLogoClick(event: MouseEvent): void {
        event.preventDefault();
        this.router.navigate(['/home']);
    }

    onNavSelect(navId: string): void {
        this.router.navigateByUrl(navId);
    }

    onWatchlistClick(): void {
        this.router.navigate(['/profiles/watchlist']);
    }

    onMenuItemSelect(menuId: string): void {
        this.router.navigateByUrl(menuId);
    }

    /** Sign in and Register share the Auth0 universal-login flow, exactly like
     *  the legacy "Sign in or register" label did. */
    onSignIn(): void {
        this.store.dispatch({ type: '[UI] Login Btn Clicked' });
    }

    /** The exact sign-out the shared-lib logout button performs (its logout()
     *  is this dispatch + storage clear), done directly instead of clicking a
     *  hidden component. Dispatched as a plain action object like onSignIn():
     *  the lib doesn't export its auth-ui action classes, and NgRx effects
     *  match on the type string. */
    onSignOut(): void {
        this.store.dispatch({ type: '[UI] Logout Btn Clicked' });
        this.tokenStorageService.clearAppStorage();
    }

    private syncFromUrl(url: string): void {
        const match: VbHeaderNavItem | undefined = this.navItems.find(
            (item: VbHeaderNavItem) =>
                url === item.id ||
                url.startsWith(`${item.id}/`) ||
                url.startsWith(`${item.id}?`),
        );
        this.activeNavId = match?.id ?? '';
    }

    /** Header avatar/menu identity — same name/initials derivation as the
     *  legacy header's Auth0 avatar fallback. */
    private toHeaderUser(user: User): VbHeaderUser {
        const firstname: string | undefined = user['firstname'] as
            | string
            | undefined;
        const lastname: string | undefined = user['lastname'] as
            | string
            | undefined;
        const email: string = (user['email'] as string | undefined) ?? '';

        let initials: string;
        if (firstname && lastname) {
            initials = firstname[0] + lastname[0];
        } else if (firstname) {
            initials = firstname.substring(0, 2);
        } else {
            initials = email.substring(0, 2);
        }

        const name: string =
            firstname && lastname
                ? `${firstname} ${lastname}`
                : firstname || email || String(user['username'] ?? 'Account');

        return {
            name,
            initials: initials.toUpperCase(),
            role: 'Bidder',
        };
    }
}
